import {FC, useRef} from 'react'

import {ContentLayoutWithSidebar} from '@/components/common/content/ContentLayoutWithSidebar'
import {usePagination} from '@/components/common/content/pagination/hooks'
import {Pagination} from '@/components/common/content/pagination/Pagination'
import {TextVariant} from '@/components/common/content/Text'
import {useMobile} from '@/components/context/MobileProvider'
import {getTrademarkDisputesContent} from '@/content/trademarkDisputes'
import {useTranslation} from '@/i18n'
import {HEADER_HEIGHT_DESKTOP} from '@/theme/sizing'
import {theme} from '@/theme/theme'
import {RelatedService} from '@/utils/related-services/types'
import {MOBILE_SCREENSIZE} from '@/utils/styles/breakpoints'

import {RelatedIpCaseBySubcategory} from './RelatedIpCaseBySubcategory'

type RelatedIpCasesBySubcategoryProps = {
  relatedCases: {
    id: string
    name: string
    caseNumber: string
    dateOfDecision: string
    jurisdiction: string
    linkHref: string
    subjectMatter: string
  }[]
  relatedService: RelatedService
}

export const RelatedIpCasesBySubcategory: FC<RelatedIpCasesBySubcategoryProps> = ({
  relatedCases,
  relatedService,
}) => {
  const {t} = useTranslation()
  const {selectedCasesTitle} = getTrademarkDisputesContent()
  const sectionRef = useRef<HTMLDivElement>(null)
  const isMobile = useMobile()

  const perPage = 10
  const hasPagination = relatedCases.length > perPage
  const {currentPage, setCurrentPage, numberOfPages} = usePagination(relatedCases, perPage)

  const sectionOffset = parseInt(isMobile ? '0px' : HEADER_HEIGHT_DESKTOP, 10) + 32

  return (
    <>
      <ContentLayoutWithSidebar
        ref={sectionRef}
        layoutTitle={t(selectedCasesTitle)}
        layoutTitleVariant={TextVariant.h2}
        relatedServices={[relatedService]}
      >
        {/* Every case is in the HTML, so crawlers reach them all; other pages are only hidden. */}
        {relatedCases.map(
          (
            {id, name, linkHref, caseNumber, dateOfDecision, jurisdiction, subjectMatter},
            index,
          ) => (
            <RelatedIpCaseBySubcategory
              key={id}
              isHidden={Math.floor(index / perPage) + 1 !== currentPage}
              name={name}
              linkHref={linkHref}
              caseNumber={caseNumber}
              dateOfDecision={dateOfDecision}
              jurisdiction={jurisdiction}
              subjectMatter={subjectMatter}
            />
          ),
        )}
        {hasPagination && (
          <div className="pagination">
            <Pagination
              sectionRef={sectionRef}
              sectionOffset={sectionOffset}
              numberOfPages={numberOfPages}
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
            />
          </div>
        )}
      </ContentLayoutWithSidebar>

      <style jsx>{`
        .pagination {
          margin-top: ${theme.spacing[7]};
        }
        @media ${MOBILE_SCREENSIZE} {
          .pagination {
            margin-top: ${theme.spacing[6]};
          }
        }
      `}</style>
    </>
  )
}
